import React, { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { programContent } from '../../data/program';
import ProgramNav from '../../components/program/ProgramNav';
import AnimatedSection from '../../components/common/AnimatedSection';
import { BookOpen, Landmark, Briefcase, Users, ArrowRight, FileText } from 'lucide-react';

const ProgramChaptersPage = () => {
  const { language } = useLanguage();
  const location = useLocation();
  const content = programContent[language]?.chapters || programContent.en.chapters;

  const transformationConfig = {
    Institutional: {
      color: 'bg-[#5B2D8E]/10 text-[#5B2D8E] border-[#5B2D8E]/20',
      icon: Landmark,
      badge: 'bg-[#5B2D8E] text-white'
    },
    Economic: {
      color: 'bg-[#D4880D]/10 text-[#D4880D] border-[#D4880D]/20',
      icon: Briefcase,
      badge: 'bg-[#D4880D] text-white'
    },
    Social: {
      color: 'bg-[#0D7377]/10 text-[#0D7377] border-[#0D7377]/20',
      icon: Users,
      badge: 'bg-[#0D7377] text-white'
    }
  };

  // Scroll to hash on mount
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash) {
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 500);
    }
  }, [location.hash]);

  return (
    <div className="pt-24">
      <ProgramNav />
      
      {/* Hero */}
      <section className="section bg-gradient-to-b from-[#FFFEF9] to-[#F5F0E8]">
        <div className="container mx-auto px-4 lg:px-10">
          <div className="max-w-4xl mx-auto text-center">
            <AnimatedSection>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#5B2D8E]/5 rounded-full mb-6">
                <BookOpen className="w-4 h-4 text-[#5B2D8E]" />
                <span className="text-sm font-medium text-[#5B2D8E]">Complete Political Program</span>
              </div>
            </AnimatedSection>
            
            <AnimatedSection delay={0.1}>
              <h1 className="heading-display mb-6">{content.hero.title}</h1>
            </AnimatedSection>
            
            <AnimatedSection delay={0.2}>
              <p className="text-xl text-[#6B6B6B]" style={{ fontFamily: 'var(--font-serif)' }}>
                {content.hero.subtitle}
              </p>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-12 bg-[#FFFEF9]">
        <div className="container mx-auto px-4 lg:px-10">
          <div className="max-w-4xl mx-auto">
            <AnimatedSection>
              <p className="text-lg text-[#2D2D2D] leading-relaxed text-center" style={{ fontFamily: 'var(--font-serif)' }}>
                {content.intro}
              </p>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Quick Navigation */}
      <section className="py-8 bg-[#F5F0E8] sticky top-[120px] lg:top-[136px] z-20 border-b border-[#E8E0D4]">
        <div className="container mx-auto px-4 lg:px-10">
          <div className="flex flex-wrap justify-center gap-3">
            {content.parts.map((part) => {
              const config = transformationConfig[part.transformation];
              return (
                <a
                  key={part.number}
                  href={`#part-${part.number}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium border transition-all hover:scale-105 ${config.color}`}
                >
                  Part {part.number}
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* Transformation Legend */}
      <section className="py-8 bg-[#FFFEF9]">
        <div className="container mx-auto px-4 lg:px-10">
          <div className="flex flex-wrap justify-center gap-6">
            {Object.entries(transformationConfig).map(([name, config]) => {
              const Icon = config.icon;
              return (
                <div key={name} className="flex items-center gap-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${config.badge}`}>
                    {name}
                  </span>
                  <Icon className="w-4 h-4 text-[#6B6B6B]" />
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Program Parts */}
      <section className="bg-[#FFFEF9]">
        <div className="container mx-auto px-4 lg:px-10">
          <div className="max-w-4xl mx-auto">
            {content.parts.map((part, index) => {
              const config = transformationConfig[part.transformation];
              const Icon = config.icon;
              return (
                <AnimatedSection key={part.number} delay={index * 0.1}>
                  <div 
                    id={`part-${part.number}`}
                    className="py-12 border-b border-[#E8E0D4] last:border-0 scroll-mt-48"
                  >
                    {/* Part Header */}
                    <div className="flex flex-col lg:flex-row lg:items-start gap-6 mb-8">
                      <div className="flex items-center gap-4">
                        <span className="text-6xl lg:text-8xl font-bold text-[#E8E0D4]" style={{ fontFamily: 'var(--font-serif)' }}>
                          {part.number}
                        </span>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${config.badge}`}>
                            {part.transformation} Transformation
                          </span>
                        </div>
                        <h2 className="heading-2 mb-2">
                          Part {part.number}: {part.title}
                        </h2>
                        <div className="divider" />
                      </div>
                    </div>

                    {/* Part Content */}
                    <div className={`rounded-xl p-6 lg:p-8 border ${config.color}`}>
                      <div className="flex items-start gap-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${config.badge}`}>
                          <Icon className="w-6 h-6" />
                        </div>
                        <div className="flex-1">
                          <p className="text-[#6B6B6B] font-mono text-sm bg-white/50 rounded-lg p-4">
                            {part.content}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* Download / Print */}
      <section className="section bg-[#F5F0E8]">
        <div className="container mx-auto px-4 lg:px-10">
          <div className="max-w-3xl mx-auto text-center">
            <AnimatedSection>
              <FileText className="w-12 h-12 mx-auto mb-6 text-[#5B2D8E]" />
              <h2 className="heading-2 mb-4">Full Program Document</h2>
              <p className="body-large mb-8">
                The complete program text will be available for download once finalized.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button className="btn-primary opacity-50 cursor-not-allowed" disabled>
                  Download PDF [Coming soon]
                </button>
                <Link to="/program" className="btn-secondary">
                  Return to Overview
                </Link>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProgramChaptersPage;
